(() => {
  if (window.__rmxHome205Ready) return;
  window.__rmxHome205Ready = true;
  const initializedRoots = new WeakSet();
  let revealObserver;
  let conversionObserver;

  const getRevealObserver = () => {
    if (revealObserver || !('IntersectionObserver' in window)) return revealObserver;
    revealObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-visible');
        revealObserver.unobserve(entry.target);
      });
    }, { threshold: 0.08, rootMargin: '0px 0px -30px' });
    return revealObserver;
  };

  const initFinderTabs = (root) => {
    root.querySelectorAll('[data-rmx-finder]').forEach((finder) => {
      if (finder.dataset.rmxFinderReady === 'true') return;
      const panels = [...finder.querySelectorAll('[data-rmx-finder-panel]')];
      const tabs = [...finder.querySelectorAll('[data-rmx-finder-tab]')];
      if (panels.length !== 2 || tabs.length !== 2) return;
      finder.dataset.rmxFinderReady = 'true';
      const setMode = (mode, focus = false) => {
        const active = panels.find((panel) => panel.dataset.rmxFinderPanel === mode);
        const current = tabs.find((tab) => tab.dataset.rmxFinderTab === mode);
        if (!active || !current) return;
        panels.forEach((panel) => {
          const inactive = panel !== active;
          panel.hidden = inactive;
          panel.inert = inactive;
          panel.setAttribute('aria-hidden', String(inactive));
          panel.classList.toggle('is-active', !inactive);
        });
        tabs.forEach((tab) => {
          const selected = tab === current;
          tab.setAttribute('aria-selected', String(selected));
          tab.tabIndex = selected ? 0 : -1;
          tab.classList.toggle('is-active', selected);
        });
        finder.dataset.rmxFinderMode = mode;
        if (focus) current.focus({ preventScroll: true });
      };
      tabs.forEach((tab, index) => {
        tab.addEventListener('click', () => setMode(tab.dataset.rmxFinderTab));
        tab.addEventListener('keydown', (event) => {
          let next;
          if (event.key === 'ArrowRight') next = (index + 1) % tabs.length;
          else if (event.key === 'ArrowLeft') next = (index + tabs.length - 1) % tabs.length;
          else if (event.key === 'Home') next = 0;
          else if (event.key === 'End') next = tabs.length - 1;
          else return;
          event.preventDefault(); setMode(tabs[next].dataset.rmxFinderTab, true);
        });
      });
      setMode('vehicle');
    });
  };

  // Enhance the existing Reputon masonry cards in place. Do not duplicate review
  // data, remove attribution, change app configuration, or move React-owned nodes.
  const reviewControllers = new Set();
  class HomeReviewRail {
    constructor(section) {
      this.section = section;
      this.app = section.querySelector('.reviews__app');
      this.nav = section.querySelector('[data-rmx-review-navigation]');
      if (!this.app || !this.nav) return;
      this.previous = this.nav.querySelector('[data-rmx-review-prev]');
      this.next = this.nav.querySelector('[data-rmx-review-next]');
      this.status = this.nav.querySelector('[data-rmx-review-position]');
      this.abort = new AbortController();
      this.cards = [];
      this.marked = new Set();
      const signal = this.abort.signal;
      this.previous.addEventListener('click', () => this.step(-1), { signal });
      this.next.addEventListener('click', () => this.step(1), { signal });
      this.observer = new MutationObserver(() => this.schedule());
      this.observer.observe(this.app, { childList: true, subtree: true });
      this.resize = new ResizeObserver(() => this.update());
      this.resize.observe(this.app);
      reviewControllers.add(this);
      this.refresh();
    }
    mark(node, name) {
      if (!node.hasAttribute(name)) node.setAttribute(name, '');
      this.marked.add(node);
    }
    schedule() {
      if (!this.frame) this.frame = requestAnimationFrame(() => { this.frame = 0; this.refresh(); });
    }
    getLoadButton() {
      return [...this.app.querySelectorAll('button,a,[role="button"]')].find((node) => {
        const label = (node.textContent || node.getAttribute('aria-label') || '').trim().replace(/\s+/g, ' ');
        return /^(load|show) more(?: reviews?)?(?:\s*[+›»→])?$/i.test(label)
          && !node.closest('[data-rmx-review-card]') && !node.hidden;
      });
    }
    resetRail() {
      this.railEvents?.abort();
      this.marked.forEach((node) => {
        [...node.attributes].forEach((a) => { if (a.name.startsWith('data-rmx-review-')) node.removeAttribute(a.name); });
      });
      this.marked.clear();
      this.rail = null;
      this.nav.hidden = true;
    }
    refresh() {
      if (!this.app.isConnected) return;
      // A merchant-selected native carousel should retain its own swipe controls.
      if (this.app.querySelector('.reputon-carousel-container')) { this.resetRail(); return; }
      const selector = '.reputon-card,.reputon-review-card';
      const cards = [...this.app.querySelectorAll(selector)].filter((card) => !card.querySelector(selector));
      if (!cards.length) { this.resetRail(); return; }
      let rail = cards[0].parentElement;
      while (rail && !cards.every((card) => rail.contains(card))) rail = rail.parentElement;
      if (!rail || (rail !== this.app && !this.app.contains(rail))) return;
      if (this.rail !== rail) {
        this.resetRail();
        this.rail = rail;
        this.railEvents = new AbortController();
        const signal = this.railEvents.signal;
        rail.addEventListener('scroll', () => this.update(), { passive: true, signal });
        rail.addEventListener('keydown', (event) => {
          if (event.target !== rail || !['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
          event.preventDefault();
          if (event.key === 'Home') this.go(0);
          else if (event.key === 'End') this.go(this.cards.length - 1);
          else this.step(event.key === 'ArrowRight' ? 1 : -1);
        }, { signal });
        if (!rail.id) rail.id = `rmx-review-rail-${this.section.querySelector('h2')?.id || reviewControllers.size}`;
        if (!rail.hasAttribute('tabindex')) rail.tabIndex = 0;
        if (!rail.hasAttribute('aria-label')) rail.setAttribute('aria-label', 'Customer reviews. Scroll sideways for more.');
        this.previous.setAttribute('aria-controls', rail.id);
        this.next.setAttribute('aria-controls', rail.id);
      }
      this.cards = cards;
      this.mark(rail, 'data-rmx-review-rail');
      cards.forEach((card) => {
        this.mark(card, 'data-rmx-review-card');
        for (let parent = card.parentElement; parent && parent !== rail; parent = parent.parentElement) this.mark(parent, 'data-rmx-review-group');
      });
      for (let parent = rail.parentElement; parent && this.app.contains(parent); parent = parent.parentElement) this.mark(parent, 'data-rmx-review-shell');
      const load = this.getLoadButton();
      if (load) this.mark(load, 'data-rmx-review-load');
      this.app.querySelectorAll('.reputon-swiper-button-prev,.reputon-swiper-button-next').forEach((node) => this.mark(node, 'data-rmx-review-old-nav'));
      this.nav.hidden = cards.length < 2 && !load;
      if (this.loading && cards.length > this.previousCount) {
        clearTimeout(this.loadTimer);
        this.loading = false;
        this.go(this.previousCount);
      }
      this.update();
    }
    currentIndex() {
      if (!this.rail || !this.cards.length) return 0;
      const left = this.rail.getBoundingClientRect().left;
      let selected = 0, distance = Infinity;
      this.cards.forEach((card, i) => {
        const delta = Math.abs(card.getBoundingClientRect().left - left);
        if (delta < distance) { distance = delta; selected = i; }
      });
      return selected;
    }
    setStatus(value) { if (this.status.textContent !== value) this.status.textContent = value; }
    update() {
      if (!this.rail || !this.cards.length) return;
      const index = this.currentIndex();
      const load = this.getLoadButton();
      this.previous.disabled = this.loading || index === 0;
      this.next.disabled = this.loading || (index === this.cards.length - 1 && (!load || load.disabled || load.getAttribute('aria-disabled') === 'true'));
      this.setStatus(this.loading ? 'Loading more reviews…' : `${index + 1} of ${this.cards.length}`);
    }
    go(index) {
      const card = this.cards[index];
      if (!card || !this.rail) return;
      const left = card.getBoundingClientRect().left - this.rail.getBoundingClientRect().left + this.rail.scrollLeft;
      this.rail.scrollTo({ left, behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
      this.update();
    }
    step(direction) {
      if (this.loading || !this.rail) return;
      const index = this.currentIndex() + direction;
      if (index >= 0 && index < this.cards.length) { this.go(index); return; }
      const load = this.getLoadButton();
      if (direction < 0 || !load || load.disabled || load.getAttribute('aria-disabled') === 'true') return;
      this.previousCount = this.cards.length;
      this.loading = true;
      this.update();
      // The user's Next click invokes the app's own paging action. Newly added
      // cards stay in the horizontal row rather than increasing the page height.
      load.click();
      this.loadTimer = setTimeout(() => {
        this.loading = false; this.refresh();
        this.setStatus('More reviews are not available yet. Try again or open the reviews page.');
      }, 12000);
    }
    destroy() {
      this.observer?.disconnect(); this.resize?.disconnect(); this.abort?.abort();
      this.railEvents?.abort(); cancelAnimationFrame(this.frame); clearTimeout(this.loadTimer);
      reviewControllers.delete(this);
    }
  }

  const initReveal = (root) => {
    const items = root.querySelectorAll('.reveal:not(.is-visible)');
    const observer = getRevealObserver();
    if (!observer) {
      items.forEach((item) => item.classList.add('is-visible'));
      return;
    }
    items.forEach((item) => observer.observe(item));
  };

  const initRoot = (root) => {
    if (!root || initializedRoots.has(root)) return;
    initializedRoots.add(root);
    initFinderTabs(root);
    root.querySelectorAll('[data-rmx-home-reviews]').forEach((section) => new HomeReviewRail(section));
    initReveal(root);
  };

  const initMobileConversion = () => {
    const stickyBar = document.getElementById('rmx-mobile-conversion');
    const finalCta = document.getElementById('rmx-final-cta');
    if (!stickyBar || !finalCta || stickyBar.dataset.ready === 'true') return;

    stickyBar.dataset.ready = 'true';
    if (!('IntersectionObserver' in window)) return;

    conversionObserver?.disconnect();
    conversionObserver = new IntersectionObserver(([entry]) => {
      stickyBar.classList.toggle('is-hidden', entry.isIntersecting);
    }, { threshold: 0.15 });
    conversionObserver.observe(finalCta);
  };

  const initAll = (scope = document) => {
    scope.querySelectorAll?.('.rmx-hp').forEach(initRoot);
    if (scope.matches?.('.rmx-hp')) initRoot(scope);
    initMobileConversion();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll(), { once: true });
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    reviewControllers.forEach((controller) => { if (event.target.contains(controller.section)) controller.destroy(); });
    conversionObserver?.disconnect();
    conversionObserver = undefined;
    document.querySelectorAll('#rmx-mobile-conversion').forEach((bar) => delete bar.dataset.ready);
    initMobileConversion();
  });
})();
